/**
 * WaypointsList Component - Display and manage passage waypoints
 */

import React from 'react';
import { Map, Plus, Route, Compass, Trash2 } from 'lucide-react';
import { Badge } from '../../../catalyst/badge';
import { Button } from '../../../catalyst/button';
import DESIGN from '../designSystem';

const WaypointsList = ({ 
  waypoints, 
  isEditing, 
  onAddWaypoint, 
  onRemoveWaypoint,
  onClearAll 
}) => {
  if (waypoints.length === 0) {
    return (
      <div className="p-5 text-center">
        <Map className="h-10 w-10 text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-500 mb-3">
          {isEditing 
            ? 'No waypoints added yet. Click on the chart below or add manually.'
            : 'Select a passage plan or create a new one to get started.'}
        </p>
        {isEditing && (
          <Button color="indigo" onClick={onAddWaypoint}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Waypoint
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={DESIGN.header.padding + ' ' + DESIGN.header.borderBottom}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={DESIGN.header.title}>Passage Waypoints</h3>
            <p className={DESIGN.header.subtitle}>
              {isEditing ? 'Click chart or add manually to build your route' : 'View passage waypoints'}
            </p>
          </div>
          {isEditing && waypoints.length > 0 && (
            <Button 
              color="white" 
              size="sm" 
              onClick={() => window.confirm('Clear all waypoints?') && onClearAll()}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </div>
      
      <div className="divide-y divide-gray-200">
        {waypoints.map((wp, index) => (
          <div key={wp.id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Badge color="blue">{wp.name}</Badge>
                  <span className="text-sm text-gray-500">
                    {wp.lat.toFixed(4)}°, {wp.lng.toFixed(4)}°
                  </span>
                </div>
                {index > 0 && (
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Route className="h-3 w-3" />
                      {wp.distance?.toFixed(1)} nm
                    </span>
                    <span className="flex items-center gap-1">
                      <Compass className="h-3 w-3" />
                      {Math.round(wp.bearing || 0)}°
                    </span>
                  </div>
                )}
                {wp.notes && (
                  <p className="text-sm text-gray-600 mt-1">{wp.notes}</p>
                )}
              </div>
              {isEditing && (
                <Button
                  color="white"
                  size="xs"
                  onClick={() => onRemoveWaypoint(wp.id)}
                  className="ml-2"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {isEditing && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <Button color="white" onClick={onAddWaypoint} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Waypoint
          </Button>
        </div>
      )}
    </>
  );
};

export default WaypointsList;